import type { GroupEntry } from "@innoclouds/db";
import { useTranslation } from "i18next-vue";
import { computed, type MaybeRefOrGetter, toValue, watch } from "vue";
import { useHotmenuStore } from "#/store/hotmenu";
import { useLikesStore } from "#/store/likes";
import { isRootGroup, isVisibleEntity } from "#/utils/hotmenu";

export const MOST_LIKED_GROUP_ID = "__most_liked__";
export const MOST_LIKED_MIN_PRODUCTS = 3;

export const useProductLike = (productId: MaybeRefOrGetter<string | null | undefined>) => {
	const likesStore = useLikesStore();
	const id = computed(() => toValue(productId));

	const isLikesEnabled = computed(() => likesStore.isEnabled);
	const isLiked = computed(() => (id.value ? likesStore.isLiked(id.value) : false));
	const isLikePending = computed(() => (id.value ? likesStore.isPending(id.value) : false));
	const likeCount = computed(() => (id.value ? likesStore.getCount(id.value) : 0));
	const toggleLike = () => {
		if (id.value) likesStore.toggleLike(id.value);
	};

	return { isLikesEnabled, isLiked, isLikePending, likeCount, toggleLike };
};

export const useLikesCountSync = () => {
	const hotmenu = useHotmenuStore();
	const likesStore = useLikesStore();

	watch(
		() => hotmenu.products,
		(products) => {
			for (const product of products.values()) likesStore.setCount(product.id, product.likes_count);
		},
		{ immediate: true },
	);
};

export const useRootGroupsWithMostLiked = () => {
	const hotmenu = useHotmenuStore();
	const likesStore = useLikesStore();
	const { t } = useTranslation();

	const hasMostLiked = computed(() => {
		if (!likesStore.isEnabled) return false;
		let count = 0;
		for (const product of hotmenu.products.values()) {
			if (isVisibleEntity(product) && likesStore.getCount(product.id) > 0) count += 1;
			if (count >= MOST_LIKED_MIN_PRODUCTS) return true;
		}
		return false;
	});

	return computed(() => {
		if (!hotmenu.groups.size) return [];

		const groups = Array.from(hotmenu.groups.values()).filter(isRootGroup);
		if (hasMostLiked.value) groups.push({ id: MOST_LIKED_GROUP_ID, name: `♥ ${t("likes.most-liked.title")}` } as GroupEntry);
		return groups;
	});
};
